/**
 * web-account-pages D2: the messages panel's one stream from the two rooms (Secure and Telegram),
 * each read with its own `GET /chat` cursor. Pure: no React, no fetching. The panel keeps one
 * `RoomFeed` per room it reads, folds every response in with the `absorb*` functions, and draws
 * `mergeStream(...).shown`.
 *
 * - The merge renders the continuous newest part only. A room that still has older history holds
 *   the stream back at its boundary (the oldest history entry fetched for it): its unfetched
 *   messages all sort before that entry, so only what sorts at or after the newest boundary is
 *   shown, and the rest waits in `buffered` until paging older reaches it. A room with no more
 *   history sets no boundary. Paging asks only the rooms whose boundary holds the stream back.
 * - Ties order by time, then room (Telegram, then Secure), then the room's own order (notices by
 *   id before messages, messages by sequence), so the order agrees with each room's own pages.
 * - A room's `throughSeq` comes from its first page and its refreshes only: an older page or a
 *   catch-up page carries a newer snapshot without the messages between, so taking theirs would
 *   skip messages on the next `afterSeq` and acknowledge unseen ones.
 * - The read acknowledgement (`readToAcknowledge`) goes out only while the panel is open and the
 *   page visible, after every catch-up page is fetched, and only when every unread studio message
 *   of the room up to `throughSeq` is in the shown part. The Secure room's unread are its newest
 *   `secureUnread` studio messages; the Telegram room's reads carry no unread count, so every
 *   studio message it holds above the last acknowledgement counts (an empty `catchUp` alone never
 *   permits one: the server leaves out of it the unread messages already in `entries`).
 */
import type { ClientThreadEntry, ClientThreadResponse, ThreadRoom } from '@telify/shared';
import { THREAD_ROOMS } from '@telify/shared/browser';
import { applyClientLedgerRefresh, compareClientEntries, mergeClientEntries } from '@/lib/client/clientChatThread';
import { withoutRemoved } from '@/lib/chat/olderPageGuard';

/** What the panel holds for one room, as read under one filter's server query. */
export interface RoomFeed {
  readonly room: ThreadRoom;
  /** False until the room's first page is folded in. */
  readonly loaded: boolean;
  /** Every entry fetched for the room (pages, catch-up, refreshes, sends), in the room's display order. */
  readonly entries: readonly ClientThreadEntry[];
  /** The oldest history entry fetched: where this room holds the stream back while `cursor` is set. */
  readonly boundary: ClientThreadEntry | null;
  /** The older-page cursor; null once the room's history is exhausted. */
  readonly cursor: string | null;
  /** The next catch-up page's `catchUpAfterSeq`; null when every catch-up page is fetched. */
  readonly catchUpNext: number | null;
  /** Highest `throughSeq` of the first page and refreshes: the next `afterSeq` and the acknowledgement. */
  readonly throughSeq: number;
  /** Highest `throughSeq` acknowledged for this room. */
  readonly ackedSeq: number;
  /** The Secure room's unread studio messages, from its latest response or read answer; null for Telegram. */
  readonly unread: number | null;
  /** The latest `serverTime` of the first page or a refresh (the next `ledgerSince` minus the overlap). */
  readonly serverTime: string | null;
}

/** One entry of the stream with the room it was read from. */
export interface StreamItem {
  readonly room: ThreadRoom;
  readonly entry: ClientThreadEntry;
}

export interface MergedStream {
  /** The continuous newest part, oldest first: what the panel draws. */
  readonly shown: readonly StreamItem[];
  /** Fetched entries held behind a boundary until paging reaches them, oldest first. */
  readonly buffered: readonly StreamItem[];
  /** True while anything older remains (a room with a boundary, so held or unfetched entries). */
  readonly hasOlder: boolean;
  /** The rooms to ask for their next older page: the ones whose boundary holds the stream back. */
  readonly pageRooms: readonly ThreadRoom[];
}

/** Whether a filter keeps an entry of a room (the filter table's `webFilterKeeps`). Default: everything. */
export type StreamKeeps = (room: ThreadRoom, entry: ClientThreadEntry) => boolean;

export interface PanelGate {
  /** The messages panel is open. */
  readonly open: boolean;
  /** The page is visible (`document.visibilityState`). */
  readonly visible: boolean;
}

export function emptyRoomFeed(room: ThreadRoom): RoomFeed {
  return {
    room, loaded: false, entries: [], boundary: null, cursor: null, catchUpNext: null, throughSeq: 0, ackedSeq: 0, unread: null, serverTime: null,
  };
}

const seqOf = (entry: ClientThreadEntry): number | null => (entry.kind === 'automatic' ? null : entry.seq);

/** A studio message of the room's conversation (the client's read marker counts these). */
const isStudioMessage = (entry: ClientThreadEntry): entry is Exclude<ClientThreadEntry, { kind: 'automatic' }> =>
  entry.kind !== 'automatic' && entry.direction === 'outbound';

const oldest = (a: ClientThreadEntry | null, page: readonly ClientThreadEntry[]): ClientThreadEntry | null =>
  page.reduce<ClientThreadEntry | null>((low, entry) => (low === null || compareClientEntries(entry, low) < 0 ? entry : low), a);

/** Where the catch-up continues: past the highest sequence of a capped page, else done. */
function catchUpNextOf(response: ClientThreadResponse): number | null {
  if (!response.catchUpHasMore) return null;
  const seqs = response.catchUp.map(seqOf).filter((seq): seq is number => seq !== null);
  return seqs.length > 0 ? Math.max(...seqs) : null;
}

const unreadOf = (feed: RoomFeed, response: ClientThreadResponse) => (feed.room === 'secure' ? response.secureUnread : null);

/** The room's first page: its history, its unread catch-up, its cursor, boundary and snapshot. */
export function absorbFirstPage(feed: RoomFeed, response: ClientThreadResponse): RoomFeed {
  return {
    ...feed,
    loaded: true,
    entries: mergeClientEntries(feed.entries, [...response.entries, ...response.catchUp]),
    boundary: oldest(null, response.entries),
    cursor: response.cursor,
    catchUpNext: catchUpNextOf(response),
    throughSeq: Math.max(feed.throughSeq, response.throughSeq),
    unread: unreadOf(feed, response),
    serverTime: response.serverTime,
  };
}

/**
 * An older page (`cursor`): its entries join and the boundary moves older. `removed` is the older
 * page guard's set (lib/chat/olderPageGuard): ids a refresh removed while this page was in flight.
 */
export function absorbOlderPage(feed: RoomFeed, response: ClientThreadResponse, removed: ReadonlySet<string> = new Set()): RoomFeed {
  return {
    ...feed,
    entries: mergeClientEntries(feed.entries, withoutRemoved([...response.entries, ...response.catchUp], removed)),
    boundary: oldest(feed.boundary, response.entries),
    cursor: response.cursor,
  };
}

/** A catch-up page (`catchUpAfterSeq`, from `catchUpNext`): its unread join until none remain. */
export function absorbCatchUpPage(feed: RoomFeed, response: ClientThreadResponse): RoomFeed {
  return {
    ...feed,
    entries: mergeClientEntries(feed.entries, [...response.entries, ...response.catchUp]),
    catchUpNext: catchUpNextOf(response),
    unread: unreadOf(feed, response),
  };
}

/**
 * A refresh (`afterSeq`, plus `ledgerSince` for Telegram): the new messages, the ledger's removals and
 * upserts, the newer snapshot. Its catch-up repeats unread the feed already holds (new ones arrive in
 * the `afterSeq` delta), so the catch-up state stays as it is.
 */
export function absorbRefresh(feed: RoomFeed, response: ClientThreadResponse): RoomFeed {
  const merged = mergeClientEntries(feed.entries, [...response.entries, ...response.catchUp]);
  return {
    ...feed,
    entries: applyClientLedgerRefresh(merged, response.ledger, feed.cursor !== null),
    throughSeq: Math.max(feed.throughSeq, response.throughSeq),
    unread: unreadOf(feed, response),
    serverTime: response.serverTime,
  };
}

/** Entries learned outside a read: the client's own send (its placeholder, then the stored row). */
export function absorbSent(feed: RoomFeed, entries: readonly ClientThreadEntry[]): RoomFeed {
  return { ...feed, entries: mergeClientEntries(feed.entries, entries) };
}

/**
 * The read acknowledgement went out for `throughSeq`. `secureUnread` is the read answer's count, for
 * the Secure room. On a failed POST the panel keeps the feed it had, so the next fetch retries.
 */
export function markAcknowledged(feed: RoomFeed, throughSeq: number, secureUnread?: number): RoomFeed {
  return {
    ...feed,
    ackedSeq: Math.max(feed.ackedSeq, throughSeq),
    unread: feed.room === 'secure' && secureUnread !== undefined ? secureUnread : feed.unread,
  };
}

const roomRank = (room: ThreadRoom) => THREAD_ROOMS.indexOf(room);

/** Stream order: time, then room (Telegram, then Secure), then the room's own order. */
export function compareStreamItems(a: StreamItem, b: StreamItem): number {
  const byTime = Date.parse(a.entry.at) - Date.parse(b.entry.at);
  if (byTime !== 0) return byTime;
  if (a.room !== b.room) return roomRank(a.room) - roomRank(b.room);
  return compareClientEntries(a.entry, b.entry);
}

const keepAll: StreamKeeps = () => true;

/**
 * The one stream from the rooms a filter reads. Nothing shows until every room's first page is in,
 * since a room not yet read could hold messages between any two shown ones.
 */
export function mergeStream(feeds: readonly RoomFeed[], keeps: StreamKeeps = keepAll): MergedStream {
  const items = feeds
    .flatMap((feed) => feed.entries.map((entry) => ({ room: feed.room, entry })))
    .filter((item) => keeps(item.room, item.entry))
    .sort(compareStreamItems);
  if (feeds.some((feed) => !feed.loaded)) return { shown: [], buffered: items, hasOlder: false, pageRooms: [] };

  const holding = feeds.filter((feed) => feed.cursor !== null);
  // A room with older history but no history entry fetched (never expected): hold everything, page it.
  const blind = holding.filter((feed) => feed.boundary === null);
  if (blind.length > 0) return { shown: [], buffered: items, hasOlder: true, pageRooms: blind.map((feed) => feed.room) };

  let edge: StreamItem | null = null;
  for (const feed of holding) {
    const boundary = { room: feed.room, entry: feed.boundary! };
    if (edge === null || compareStreamItems(boundary, edge) > 0) edge = boundary;
  }
  if (edge === null) return { shown: items, buffered: [], hasOlder: false, pageRooms: [] };
  const limit = edge;
  const first = items.findIndex((item) => compareStreamItems(item, limit) >= 0);
  const split = first < 0 ? items.length : first;
  return {
    shown: items.slice(split),
    buffered: items.slice(0, split),
    hasOlder: true,
    pageRooms: holding.filter((feed) => compareStreamItems({ room: feed.room, entry: feed.boundary! }, limit) === 0).map((feed) => feed.room),
  };
}

/**
 * The `throughSeq` to acknowledge for this room now (`POST /chat/read`), or null. Only what the client
 * has been shown is read: the panel open and the page visible, every catch-up page fetched, and every
 * unread studio message of the room up to `throughSeq` in the stream's shown part (so a filter that
 * hides them, or the merge buffer holding them, acknowledges nothing).
 */
export function readToAcknowledge(stream: MergedStream, feed: RoomFeed, gate: PanelGate): number | null {
  if (!gate.open || !gate.visible) return null;
  if (!feed.loaded || feed.catchUpNext !== null) return null;
  const { throughSeq, ackedSeq } = feed;
  if (throughSeq <= ackedSeq) return null;
  let unread = feed.entries
    .filter(isStudioMessage)
    .filter((entry) => entry.seq > ackedSeq && entry.seq <= throughSeq)
    .sort((a, b) => b.seq - a.seq);
  if (feed.unread !== null) unread = unread.slice(0, feed.unread);
  if (unread.length === 0) return null;
  const shown = new Set(stream.shown.filter((item) => item.room === feed.room).map((item) => item.entry.id));
  return unread.every((entry) => shown.has(entry.id)) ? throughSeq : null;
}
